import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Spinner, Alert, Badge, ListGroup } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle, faSave, faSchool, faMapMarkedAlt, faKey } from '@fortawesome/free-solid-svg-icons';
import UserService from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const Profile = () => {
  const { user, setUser } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birth_date: '',
    cpf: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await UserService.getMe();
      setUserData(data);
      setFormData({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        birth_date: data.birth_date || '',
        cpf: data.cpf || ''
      });
    } catch (err) {
      setError('Erro ao carregar dados do perfil.');
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        birth_date: formData.birth_date || null,
        cpf: formData.cpf || null
      };
      
      const updatedUser = await UserService.partialUpdate(user.id, payload);
      
      // Atualiza o contexto de autenticação com os novos dados
      setUser({ ...user, ...updatedUser });
      
      showToast('Perfil atualizado com sucesso!', 'success');
    } catch (err) {
      console.error('Error updating profile:', err);
      showToast('Erro ao atualizar perfil. Tente novamente.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      showToast('A nova senha e a confirmação não coincidem.', 'warning');
      return;
    }

    if (passwordData.new_password.length < 6) {
      showToast('A nova senha deve ter pelo menos 6 caracteres.', 'warning');
      return;
    }

    try {
      setChangingPassword(true);
      const payload = {
        last_password: passwordData.current_password,
        password: passwordData.new_password,
        password_confirmation: passwordData.confirm_password
      };
      await UserService.changePassword(payload);
      
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      showToast('Senha alterada com sucesso!', 'success');
    } catch (err) {
      console.error('Error changing password:', err);
      let errorMsg = 'Erro ao alterar senha. Tente novamente.';
      
      if (err.response?.data) {
        if (err.response.data.non_field_errors) {
          errorMsg = err.response.data.non_field_errors[0];
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        } else if (err.response.data.last_password) {
          errorMsg = err.response.data.last_password[0];
        } else if (err.response.data.password) {
          errorMsg = err.response.data.password[0];
        }
      }
      
      showToast(errorMsg, 'danger');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        {error}
      </Alert>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Meu Perfil</h4>
          <p className="mb-0">Gerencie suas informações pessoais</p>
        </div>
      </div>

      <Row>
        <Col xs={12} xl={8} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUserCircle} className="me-2" />
                Informações Pessoais
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label>Nome Completo</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Digite seu nome completo"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>E-mail</Form.Label>
                      <Form.Control 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="seu@email.com"
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Telefone</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(00) 00000-0000"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Data de Nascimento</Form.Label>
                      <Form.Control 
                        type="date" 
                        name="birth_date"
                        value={formData.birth_date}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>CPF</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleInputChange}
                        placeholder="000.000.000-00"
                        maxLength="14"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-3">
                  <Button 
                    variant="primary" 
                    type="submit"
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card border="light" className="shadow-sm mt-4">
            <Card.Header>
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faKey} className="me-2" />
                Alterar Senha
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handlePasswordSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Senha Atual</Form.Label>
                  <Form.Control 
                    type="password" 
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    placeholder="Digite sua senha atual"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Nova Senha</Form.Label>
                  <Form.Control 
                    type="password" 
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    placeholder="Digite a nova senha"
                    required
                    minLength="6"
                  />
                  <Form.Text className="text-muted">
                    A senha deve ter pelo menos 6 caracteres.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirmar Nova Senha</Form.Label>
                  <Form.Control 
                    type="password" 
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    placeholder="Confirme a nova senha"
                    required
                    minLength="6"
                  />
                </Form.Group>

                <Button 
                  variant="warning" 
                  type="submit"
                  disabled={changingPassword}
                >
                  <FontAwesomeIcon icon={faKey} className="me-2" />
                  {changingPassword ? 'Alterando...' : 'Alterar Senha'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={4} className="mb-4">
          <Card border="light" className="shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Informações da Conta</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <p className="mb-1"><strong>Status:</strong></p>
                <Badge bg={userData?.is_active ? 'success' : 'secondary'}>
                  {userData?.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
              <div className="mb-3">
                <p className="mb-1"><strong>Tipo de Usuário:</strong></p>
                <Badge bg={userData?.is_staff ? 'primary' : 'info'}>
                  {userData?.is_staff ? 'Administrador' : 'Usuário'}
                </Badge>
              </div>
            </Card.Body>
          </Card>

          {userData?.school_users && userData.school_users.length > 0 && (
            <Card border="light" className="shadow-sm mb-4">
              <Card.Header>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faSchool} className="me-2" />
                  Escolas Vinculadas
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <ListGroup variant="flush">
                  {userData.school_users.map((schoolUser) => (
                    <ListGroup.Item key={schoolUser.id}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">{schoolUser.school.name}</h6>
                          <Badge bg="info" className="me-2">
                            {schoolUser.role_display}
                          </Badge>
                          <Badge bg={schoolUser.active ? 'success' : 'secondary'}>
                            {schoolUser.active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}

          {userData?.municipality_users && userData.municipality_users.length > 0 && (
            <Card border="light" className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faMapMarkedAlt} className="me-2" />
                  Municípios Vinculados
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <ListGroup variant="flush">
                  {userData.municipality_users.map((municipalityUser) => (
                    <ListGroup.Item key={municipalityUser.id}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">{municipalityUser.municipality.name}</h6>
                        <Badge bg={municipalityUser.active ? 'success' : 'secondary'}>
                          {municipalityUser.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

export default Profile;
